import * as Astronomy from 'astronomy-engine';

/**
 * Lahiri (Chitrapaksha) ayanamsa — the sidereal offset.
 *
 * Everything downstream depends on this: sidereal longitude = tropical
 * longitude − ayanamsa, and that single subtraction decides every sign,
 * nakshatra, pada and kuta score the tools report.
 *
 * We do NOT approximate this as a linear 50.29"/yr drift. That is off by
 * several arcminutes over a century, which is enough to move a Moon near a
 * cusp into the wrong nakshatra.
 *
 * Instead we use Swiss Ephemeris's own definition: at a fixed reference
 * instant t0, the sidereal zero point lies at a known ecliptic longitude.
 * That direction is FIXED in space, so ayanamsa(t) is simply where that same
 * direction sits in the ecliptic-of-date frame at time t. We obtain it by
 * rotating through astronomy-engine's own precession matrices rather than
 * writing a precession polynomial by hand — self-consistent by construction,
 * and one less thing that can be silently mistyped.
 *
 * Lahiri is the Indian government standard (Rashtriya Panchang) and the
 * default in Parashari practice. To switch systems, change only this constant.
 */
export const AYANAMSA = {
  id: 'lahiri',
  label: 'Lahiri (Chitrapaksha)',
  /** 1956-03-21T00:00:00 TT */
  t0JD: 2435553.5,
  /** 23°14'43.88" — Swiss Ephemeris's Lahiri anchor value at t0. */
  valueAtT0Deg: 23.245522556,
} as const;

/** astronomy-engine reckons time in days from J2000 (JD 2451545.0). */
function timeFromJD(jd: number): Astronomy.AstroTime {
  return Astronomy.MakeTime(jd - 2451545.0);
}

const T0 = timeFromJD(AYANAMSA.t0JD);

function normalize360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * The sidereal zero point as a fixed direction in the J2000 equatorial frame.
 * Computed once: it does not move.
 */
const ZERO_POINT_EQJ: Astronomy.Vector = (() => {
  const lonRad = (AYANAMSA.valueAtT0Deg * Math.PI) / 180;
  // Unit vector at (lon = ayanamsa(t0), lat = 0) in the true ecliptic of t0.
  const ect = new Astronomy.Vector(Math.cos(lonRad), Math.sin(lonRad), 0, T0);
  return Astronomy.RotateVector(Astronomy.Rotation_ECT_EQJ(T0), ect);
})();

/**
 * Ayanamsa in degrees at the given instant — the amount to subtract from a
 * tropical ecliptic-of-date longitude to obtain a sidereal one.
 */
export function ayanamsaDeg(time: Astronomy.AstroTime): number {
  const ect = Astronomy.RotateVector(Astronomy.Rotation_EQJ_ECT(time), ZERO_POINT_EQJ);
  const sphere = Astronomy.SphereFromVector(ect);
  return normalize360(sphere.lon);
}

/** Convenience wrapper for callers holding a Julian Day rather than an AstroTime. */
export function ayanamsaDegAtJD(jd: number): number {
  return ayanamsaDeg(timeFromJD(jd));
}

export { timeFromJD, normalize360 };
